/* global globalThis */
import React, { useState, useRef, useEffect } from "react";
import { Send, Loader2, ChevronDown, ChevronRight, User } from "lucide-react";
import logo1 from "../../../../assets/Image (3).jpg";
import PropTypes from "prop-types";
import axiosClient from "../../../../api-client/AxiosClient";
import { toast } from "react-toastify";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/cjs/styles/prism";

const BASE_URL = process.env.REACT_APP_AGENT_API_BASE_URL
const API_CODE = process.env.REACT_APP_API_CODE;


const TransformationRulePanel = ({ fileId }) => {
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [userMappingFileId, setUserMappingFileId] = useState(null);
  const [currentFileName, setCurrentFileName] = useState(() => {
    return sessionStorage.getItem("selectedFileName") || "Transformation Rule Generator";
  });

  const [expandedTargets, setExpandedTargets] = useState({});
  const [selectedTargets, setSelectedTargets] = useState({});
  const [isApproving, setIsApproving] = useState(false);

  const toggleTarget = (targetName) => {
    setExpandedTargets(prev => ({
      ...prev,
      [targetName]: !prev[targetName]
    }));
  };

  const toggleSelectTarget = (targetKey, e) => {
    e.stopPropagation(); // Prevent accordion toggle when clicking checkbox
    setSelectedTargets(prev => ({
      ...prev,
      [targetKey]: !prev[targetKey]
    }));
  };

  const handleSelectAll = (targets) => {
    // Check if all are currently selected
    const allSelected = targets.every((result, index) =>
      selectedTargets[`${result.target}-${index}`]
    );

    const newSelectAll = !allSelected;

    const newSelected = {};
    targets.forEach((result, index) => {
      const targetKey = `${result.target}-${index}`;
      newSelected[targetKey] = newSelectAll;
    });
    setSelectedTargets(newSelected);
  };
  const parseMarkdown = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);

    return parts.map((part) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const content = part.slice(2, -2);
        return <strong key={content}>{content}</strong>;
      }
      return <span key={part}>{part}</span>;
    });
  };

  // Get corporation_id from localStorage
  const getCorporationId = () => {
    try {
      const storedCorporation = localStorage.getItem("selectedCorporation");
      if (storedCorporation) {
        const parsed = JSON.parse(storedCorporation);
        return parsed.value || parsed.id;
      }
    } catch (error) {
      console.error("Error getting corporation ID:", error);
    }
    return null;
  };

  const getOrganizationConfigId = () => {
  try {
    return localStorage.getItem("organization_config_id");
  } catch (error) {
    console.error("Error getting organization config ID:", error);
    return null;
  }
};


  // Get mapping file ID from sessionStorage
  const getMappingFileId = () => {
    try {
      const storedFileId = localStorage.getItem("UserMappingFileId");
      return storedFileId || null;
    } catch (error) {
      console.error("Error getting mapping file ID:", error);
      return null;
    }
  };

  // FIX: Update file ID and reset chat when fileId prop changes
  useEffect(() => {
    if (fileId) {
      setUserMappingFileId(fileId);

      // Get the current file name from session storage
      const storedFileName = sessionStorage.getItem("selectedFileName");
      const fileName = storedFileName || "Transformation Rule Generator";
      setCurrentFileName(fileName);

      // Reset messages with new welcome message
      const welcome = {
        id: Date.now(),
        sender: "ai",
        text: `👋 Hello! I am your **${fileName}** assistant. I can help you generate transformation rules for your mapping file.`,
        timestamp: new Date(),
      };
      setMessages([welcome]);

      // Clear message input
      setInputMessage("");
    }
  }, [fileId]);

  // FIX: Listen for file changes in session storage (cross-component sync)
  useEffect(() => {
    const handleStorageChange = () => {
      const storedFileId = localStorage.getItem("UserMappingFileId");
      const storedFileName = sessionStorage.getItem("selectedFileName");

      // Update file name if changed
      if (storedFileName && storedFileName !== currentFileName) {
        setCurrentFileName(storedFileName);
      }

      // Reset chat if file ID changed
      if (storedFileId && storedFileId !== userMappingFileId) {
        setUserMappingFileId(storedFileId);

        const fileName = storedFileName || "Transformation Rule Generator";
        setCurrentFileName(fileName);

        // Reset chat with new welcome message
        const welcome = {
          id: Date.now(),
          sender: "ai",
          text: `👋 Hello! I am your **${fileName}** assistant. I can help you generate transformation rules for your mapping file.`,
          timestamp: new Date(),
        };
        setMessages([welcome]);
        setInputMessage("");
      }
    };

    // Listen for custom storage events
    globalThis.addEventListener("storage", handleStorageChange);
    globalThis.addEventListener("customStorageChange", handleStorageChange);

    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
      globalThis.removeEventListener("customStorageChange", handleStorageChange);
    };
  }, [userMappingFileId, currentFileName]);

  // NEW: Listen specifically for file name changes in sessionStorage
  useEffect(() => {
    const handleFileNameChange = () => {
      const storedFileName = sessionStorage.getItem("selectedFileName");
      if (storedFileName && storedFileName !== currentFileName) {
        setCurrentFileName(storedFileName);

        // Update the welcome message if it's the first message
        if (messages.length === 1 && messages[0].sender === "ai") {
          const updatedWelcome = {
            ...messages[0],
            text: `👋 Hello! I am your **${storedFileName}** assistant. I can help you generate transformation rules for your mapping file.`,
          };
          setMessages([updatedWelcome]);
        }
      }
    };

    // Listen for storage events
    globalThis.addEventListener("storage", handleFileNameChange);
    globalThis.addEventListener("customStorageChange", handleFileNameChange);

    // Check immediately on mount and when currentFileName changes
    handleFileNameChange();

    return () => {
      globalThis.removeEventListener("storage", handleFileNameChange);
      globalThis.removeEventListener("customStorageChange", handleFileNameChange);
    };
  }, [currentFileName, messages]);

  // Initial load - only if no fileId prop provided
  useEffect(() => {
    if (!fileId) {
      const storedFileId = localStorage.getItem("UserMappingFileId");
      const storedFileName = sessionStorage.getItem("selectedFileName");

      if (storedFileId) {
        setUserMappingFileId(storedFileId);
        setCurrentFileName(storedFileName || "Transformation Rule Generator");
      }
    }
  }, [fileId]);

  // Initial welcome message - only on first mount if no messages
  useEffect(() => {
    if (messages.length === 0) {
      const welcome = {
        id: Date.now(),
        sender: "ai",
        text: `👋 Hello! I am your **${currentFileName}** assistant. I can help you generate transformation rules for your mapping file.`,
        timestamp: new Date(),
      };
      setMessages([welcome]);
    }
  }, []);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Generate transformation rule
  const generateTransformationRule = async () => {
    if (!inputMessage.trim()) {
      return;
    }

    // Get both IDs
    const corporationId = getCorporationId();
    const mappingFileId = userMappingFileId || getMappingFileId();
    const organizationConfigId = getOrganizationConfigId();

    // Validation
    if (!corporationId) {
      const errorMsg = {
        id: Date.now(),
        sender: "ai",
        text: " Corporation ID not found. Please select a corporation first.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
      return;
    }

    if (!mappingFileId) {
      const errorMsg = {
        id: Date.now(),
        sender: "ai",
        text: " Mapping file ID not found. Please select a mapping file first.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
      return;
    }

    // Add user message
    const userMsg = {
      id: Date.now(),
      sender: "user",
      text: inputMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputMessage("");

    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/api/generate-transformation-rule?code=${API_CODE}`, 
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: inputMessage,
            organization_config_id: organizationConfigId,
            mapping_file_id: mappingFileId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);

        // Handle error response with error field
        if (errorData?.error) {
          const errorMsg = {
            id: Date.now() + 1,
            sender: "ai",
            text: `${errorData.error}`,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMsg]);
          setLoading(false);
          return;
        }

        // Handle other error formats
        if (errorData) {
          const errorMessage = errorData.message || errorData.detail || `HTTP error! status: ${response.status}`;
          throw new Error(errorMessage);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check if response itself contains an error field (even with 200 status)
      if (data.error) {
        const errorMsg = {
          id: Date.now() + 1,
          sender: "ai",
          text: ` ${data.error}`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMsg]);
        setLoading(false);
        return;
      }

      // Add AI response with rules table
      const aiMsg = {
        id: Date.now() + 1,
        sender: "ai",
        text: "Here are the generated transformation rules:",
        timestamp: new Date(),
        hasRules: true,
        rulesData: data,
      };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (error) {
      console.error("Error generating transformation rule:", error);
      const errorMsg = {
        id: Date.now() + 1,
        sender: "ai",
        text: ` ${error.message || "Failed to generate transformation rule"}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Handle Approve Selected transformation rules
  const handleApprove = async (messageId, allResults) => {
    if (isApproving) return;

    // Get selected targets
    const selectedItems = Object.entries(selectedTargets)
      .filter(([key, value]) => value)
      .map(([key]) => key);

    if (selectedItems.length === 0) {
      toast.warning("Please select at least one transformation rule to approve");
      return;
    }

    setIsApproving(true);

    try {
      const mappingFileId = userMappingFileId || getMappingFileId();

      if (!mappingFileId) {
        toast.error("Mapping file ID not found");
        setIsApproving(false);
        return;
      }

      // Build the payload
      const updateTransformationRuleDetailsRequests = selectedItems.map((itemKey) => {
        // Extract the target name from the key (format: "targetName-index")
        const targetName = itemKey.substring(0, itemKey.lastIndexOf('-'));

        // Find the corresponding result
        const result = allResults.find((r, idx) => `${r.target}-${idx}` === itemKey);

        if (!result) {
          console.warn(`Result not found for key: ${itemKey}`);
          return null;
        }

        return {
          targetTableName: result.target || targetName,
          transformationRule: result.transformation_rule || "",
        };
      }).filter(item => item !== null); // Remove null entries

      if (updateTransformationRuleDetailsRequests.length === 0) {
        toast.error("No valid transformation rules to approve");
        setIsApproving(false);
        return;
      }

      const payload = {
        updateTransformationRuleDetailsRequests: updateTransformationRuleDetailsRequests,
        userMappingFileId: mappingFileId,
      };

      console.log("Approve Payload:", payload); // Debug log

      // Call the approval API
      await axiosClient.post(
        "/api/Mapper/UpdateTransformationRule",
        payload
      );

      toast.success(`${updateTransformationRuleDetailsRequests.length} transformation rule(s) approved successfully`);

      // Reset selections after approval
      setSelectedTargets({});
    } catch (error) {
      console.error("Approval Error:", error);
      toast.error(` Failed to approve transformation rules: ${error.message}`);
    } finally {
      setIsApproving(false);
    }
  };

  // NEW: Handle Approve for single transformation rule
  const handleApproveSingle = async (targetTableName, transformationRule) => {
    if (isApproving) return;

    setIsApproving(true);

    try {
      const mappingFileId = userMappingFileId || getMappingFileId();

      if (!mappingFileId) {
        toast.error("Mapping file ID not found");
        setIsApproving(false);
        return;
      }

      const payload = {
        updateTransformationRuleDetailsRequests: [
          {
            targetTableName: targetTableName,
            transformationRule: transformationRule,
          }
        ],
        userMappingFileId: mappingFileId,
      };

      console.log("Approve Single Payload:", payload); // Debug log

      // Call the approval API
      await axiosClient.post(
        "/api/Mapper/UpdateTransformationRule",
        payload
      );

      toast.success(` Transformation rule for "${targetTableName}" approved successfully`);
    } catch (error) {
      console.error("Approval Error:", error);
      toast.error(` Failed to approve transformation rule: ${error.message}`);
    } finally {
      setIsApproving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      generateTransformationRule();
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-gray-50">
      {/* Messages area - flex-1 with overflow */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        <div className="max-w-[1100px] mx-auto space-y-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-3 ${msg.sender === "user" ? "justify-end" : "justify-start"
                }`}
            >
              {msg.sender === "ai" && (
                <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
                  <img
                    src={logo1}
                    alt="AI Logo"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div
                className={`${msg.sender === "user" ? "max-w-3xl" : "max-w-5xl"
                  }`}
              >
                <div
                  className={`px-4 py-2 rounded-lg ${msg.sender === "user"
                    ? "bg-blue-500 text-white"
                    : "bg-white border border-gray-200 text-gray-800"
                    }`}
                >
                  <p className="whitespace-pre-wrap">
                    {parseMarkdown(msg.text)}
                  </p>
                  {/* Transformation Rule Display */}
                  {msg.sender === "ai" && msg.hasRules && msg.rulesData && (
                    <div className="mt-3 space-y-2">
                      {/* Check if it's a batch response with multiple results */}
                      {msg.rulesData.results && Array.isArray(msg.rulesData.results) ? (
                        <>
                          {/* Select All Checkbox - Top */}
                          {msg.rulesData.results.some(r => r.status === "success") && (
                            <div className="mb-3 pb-3 border-b border-gray-200">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={(() => {
                                    const successResults = msg.rulesData.results.filter(r => r.status === "success");
                                    if (successResults.length === 0) return false;
                                    return successResults.every((result, index) =>
                                      selectedTargets[`${result.target}-${index}`]
                                    );
                                  })()}
                                  onChange={() => handleSelectAll(msg.rulesData.results.filter(r => r.status === "success"))}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                  Select All ({msg.rulesData.results.filter(r => r.status === "success").length})
                                </span>
                              </label>
                            </div>
                          )}
                          {/* Hierarchical Accordion View - Show ALL (Success AND Skipped) */}
                          <div className="space-y-2">
                            {msg.rulesData.results
                              .map((result, index) => {
                                const isSkipped = result.status === "skipped";

                                // For success items, use their index within the filtered success array
                                const successResults = msg.rulesData.results.filter(r => r.status === "success");
                                const successIndex = successResults.findIndex(r => r.target === result.target);
                                const targetKey = isSkipped ? `skipped-${index}` : `${result.target}-${successIndex}`;

                                const isExpanded = expandedTargets[`${msg.id}-${result.target}`];
                                const isSelected = selectedTargets[targetKey] || false;
                                return (
                                  <div
                                    key={`${msg.id}-${result.target}-${index}`}
                                    className={`border-2 ${isSkipped ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-white'} rounded-lg overflow-hidden transition-all`}
                                  >
                                    {/* Target Header - Clickable */}
                                    <button
                                      onClick={() => toggleTarget(`${msg.id}-${result.target}`)}
                                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors w-full bg-transparent border-none text-left"
                                    >
                                      <div className="flex items-center gap-3 flex-1">
                                        {/* Checkbox - Only for success */}
                                        {!isSkipped && (
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => toggleSelectTarget(targetKey, e)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                          />
                                        )}

                                        {/* Info Icon for Skipped - Replaces Checkbox */}
                                        {isSkipped && (
                                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                            <svg
                                              className="w-5 h-5 text-gray-600"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                              />
                                            </svg>
                                          </div>
                                        )}

                                        {/* Expand/Collapse Icon */}
                                        {isExpanded ? (
                                          <ChevronDown className="w-4 h-4 text-gray-600 flex-shrink-0" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
                                        )}

                                        {/* Target Name */}
                                        <span className="font-semibold text-gray-900 text-sm">
                                          {result.target}
                                        </span>
                                      </div>

                                      {/* Skipped Badge on Right
                                      {isSkipped && (
                                        <span className="px-3 py-1 bg-gray-200 text-gray-800 text-xs font-semibold rounded-full">
                                          Skipped
                                        </span>
                                      )} */}
                                      </button>

                                    {/* Expanded Content */}
                                    {isExpanded && (
                                      <div className="border-t border-gray-200">
                                        {isSkipped ? (
                                          /* Skipped Reason Display */
                                          <div className="bg-gray-50 p-4">
                                            <div className="flex items-start gap-3">
                                              <svg
                                                className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                                />
                                              </svg>
                                              <div className="flex-1">
                                                <p className="text-sm font-medium text-gray-900">
                                                  {result.reason || "No reason provided"}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        ) : (
                                          /* Success - Transformation Rule Display */
                                          <>
                                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                              <div className="flex items-center gap-2">
                                                <svg
                                                  className="w-5 h-5 text-gray-600"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
                                                  />
                                                </svg>
                                                <span className="text-sm font-semibold text-gray-900">
                                                  Transformation Rule
                                                </span>
                                              </div>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  navigator.clipboard.writeText(result.transformation_rule || "");
                                                  toast.success("Transformation rule copied to clipboard!");
                                                }}
                                                className="text-gray-600 hover:text-gray-800 hover:bg-gray-200 p-1.5 rounded transition-colors"
                                                title="Copy transformation rule"
                                              >
                                                <svg
                                                  className="w-4 h-4"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                  />
                                                </svg>
                                              </button>
                                            </div>
                                            <div className="overflow-x-auto">
                                              <SyntaxHighlighter
                                                language="sql"
                                                style={vscDarkPlus}
                                                customStyle={{
                                                  margin: 0,
                                                  padding: "1rem",
                                                  fontSize: "0.75rem",
                                                  lineHeight: "1.5",
                                                  borderRadius: 0,
                                                }}
                                                showLineNumbers={true}
                                                wrapLines={true}
                                              >
                                                {result.transformation_rule || "No rule available"}
                                              </SyntaxHighlighter>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                          {/* Select All & Approve Section */}
                          {msg.rulesData.results.some(r => r.status === "success") && (
                            <div className="mt-4 pt-3 border-t border-gray-200 flex items-center justify-end">
                              {/* Approve Button - Only show if any selected */}
                              {Object.values(selectedTargets).some(Boolean) && (
                                <button
                                  onClick={() => handleApprove(msg.id, msg.rulesData.results.filter(r => r.status === "success"))}
                                  disabled={isApproving}
                                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium text-sm ${isApproving
                                    ? "bg-gray-400 text-white cursor-not-allowed"
                                    : "bg-green-600 text-white hover:bg-green-700"
                                    }`}
                                >
                                  {isApproving ? (
                                    <>
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                      Approving...
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                      Approve Selected ({Object.values(selectedTargets).filter(Boolean).length})
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        // Single transformation rule 
                        <>
                          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-gray-900 text-sm">
                                  Target Structure:
                                </h4>
                                <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-xs font-medium">
                                  {msg.rulesData.target || "N/A"}
                                </span>
                              </div>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(msg.rulesData.transformation_rule || "");
                                  toast.success("Transformation rule copied to clipboard!");
                                }}
                                className="text-gray-600 hover:text-gray-800 hover:bg-gray-200 p-1.5 rounded transition-colors"
                                title="Copy transformation rule"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                  />
                                </svg>
                              </button>
                            </div>

                            <div className="overflow-x-auto">
                              <SyntaxHighlighter
                                language="sql"
                                style={vscDarkPlus}
                                customStyle={{
                                  margin: 0,
                                  padding: "1rem",
                                  fontSize: "0.75rem",
                                  lineHeight: "1.5",
                                  borderRadius: 0,
                                }}
                                showLineNumbers={true}
                                wrapLines={true}
                              >
                                {msg.rulesData.transformation_rule || "No rule available"}
                              </SyntaxHighlighter>
                            </div>
                          </div>

                          {/* Approve Button for Single Rule */}
                          <div className="mt-3 flex justify-end">
                            <button
                              onClick={() => handleApproveSingle(
                                msg.rulesData.target,
                                msg.rulesData.transformation_rule
                              )}
                              disabled={isApproving}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium text-sm ${isApproving
                                ? "bg-gray-400 text-white cursor-not-allowed"
                                : "bg-green-600 text-white hover:bg-green-700"
                                }`}
                            >
                              {isApproving ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Approving...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Approve
                                </>
                              )}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <span className="text-xs opacity-70 mt-1 block">
                    {msg.timestamp.toLocaleTimeString()}
                  </span>
                </div>
              </div>

              {msg.sender === "user" && (
                <div className="w-8 h-8 bg-gray-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          ))}

          {/* Typing Indicator */}
          {loading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden">
                <img
                  src={logo1}
                  alt="AI Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-white border border-gray-200 px-4 py-2 rounded-lg">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area - Sticky at bottom */}
      <div className="bg-white border-t border-gray-200 px-4 py-3 flex-shrink-0">
        <div className="max-w-4xl mx-auto">
          <div className="relative flex items-center">
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your query to generate transformation rules..."
              className="w-full pr-12 pl-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="1"
              style={{ minHeight: "48px", maxHeight: "120px" }}
              disabled={loading}
            />
            <button
              onClick={generateTransformationRule}
              disabled={loading || !inputMessage.trim()}
              className={`absolute right-3 p-2 rounded-md transition-colors ${!loading && inputMessage.trim()
                ? "text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                : "text-gray-400 cursor-not-allowed"
                }`}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">
            Press Enter to generate, Shift + Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
};

TransformationRulePanel.propTypes = {
  fileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default TransformationRulePanel;